let isSupported = false;

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  isSupported = true;
}

const estimateDurationMs = (text, rate) => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const wordsPerMinute = 130 * rate;
  const baseMs = (words / wordsPerMinute) * 60000;
  const pauses = (text.match(/[.,!?;:]/g) || []).length;
  const pauseMs = pauses * 350;
  return Math.max(600, baseMs + pauseMs);
};

// НОВОЕ: onStart — вызывается в момент, когда синтез речи РЕАЛЬНО начал
// звучать (utterance.onstart), а не в момент вызова speak(). Между этими
// двумя моментами у speechSynthesis есть непредсказуемая задержка движка
// (плюс искусственная задержка в 100мс перед speak() ниже) — именно она
// была причиной рассинхрона хлопков в задании на бдительность (letter 6c),
// где ref на "текущую букву" выставлялся в момент вызова, а не в момент
// реального звука.
//
// ПОДСТРАХОВКА: для очень коротких фраз (одна буква — как раз случай
// vigilance-задания) Chrome иногда не вызывает utterance.onstart вообще —
// задокументированная особенность браузера. Поэтому onStart гарантированно
// вызывается не позже onEnd: если onstart так и не сработал, считаем, что
// звук начался непосредственно перед finish() — не идеально точно, но не
// оставляет onStart невызванным (что было бы хуже — ref вообще не обновился
// бы вовремя).
export const speak = (text, { onEnd, onStart, rate = 0.9, lang = 'ru-RU' } = {}) => {
  if (!isSupported || !text) {
    if (onStart) onStart();
    if (onEnd) onEnd();
    return;
  }

  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel();
  }
  if (window.speechSynthesis.paused) {
    window.speechSynthesis.resume();
  }

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.rate = rate;

  let done = false;
  let started = false;
  let minTimeReached = false;
  let pollTimer = null;
  let minTimer = null;
  let hardCapTimer = null;
  let keepAliveTimer = null;

  const cleanupTimers = () => {
    clearTimeout(minTimer);
    clearInterval(pollTimer);
    clearTimeout(hardCapTimer);
    clearInterval(keepAliveTimer);
  };

  const markStarted = () => {
    if (started) return;
    started = true;
    if (onStart) onStart();
  };

  const finish = () => {
    if (done) return;
    done = true;
    cleanupTimers();
    // Гарантия: onStart вызывается не позже onEnd, даже если реальное
    // событие onstart браузер так и не прислал (короткие фразы в Chrome).
    markStarted();
    if (onEnd) onEnd();
  };

  utterance.onstart = markStarted;
  // Доп. подстраховка: onboundary (граница слова/предложения) в части
  // версий Chrome срабатывает раньше или вместо onstart для коротких фраз.
  utterance.onboundary = markStarted;
  utterance.onend = finish;
  utterance.onerror = finish;

  const estimatedMs = estimateDurationMs(text, rate);

  // Не проверяем speechSynthesis.speaking сразу — в первые доли
  // секунды после speak() браузер ещё не "включился".
  minTimer = setTimeout(() => { minTimeReached = true; }, Math.min(estimatedMs, 600));

  pollTimer = setInterval(() => {
    if (!minTimeReached) return;
    if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
      finish();
    }
  }, 200);

  // Абсолютный потолок — на случай, если synthesis зависнет насовсем.
  hardCapTimer = setTimeout(finish, Math.max(estimatedMs * 2, 15000));

  // ИЗВЕСТНЫЙ БАГ CHROME: на длинных фразах (примерно от ~13-15 секунд
  // непрерывной речи) speechSynthesis сам замолкает без вызова onend
  // или onerror — просто останавливается физически. Никакая точность
  // оценки длительности или опрос .speaking это не лечит, потому что
  // проблема не в определении конца речи, а в том, что речь реально
  // обрывается внутри браузера.
  //
  // Задокументированный обходной путь — периодически "будить" движок
  // через pause()/resume(), пока идёт речь. Это не даёт Chrome заснуть
  // на середине длинной фразы (как раз инструкция SDMT попадает под
  // эту длину).
  keepAliveTimer = setInterval(() => {
    if (done) return;
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }
  }, 10000);

  setTimeout(() => {
    window.speechSynthesis.speak(utterance);
  }, 100);
};

export const cancelSpeech = () => {
  if (isSupported) window.speechSynthesis.cancel();
};